import { css, CSSObject, keyframes } from '@emotion/react'
import {
  FontWeight,
  ThemedColors,
  Shadow,
  ShadowElevation,
  Spacing,
  Typeface,
  Typography,
  wideGamutColor,
  ZIndex,
} from '@opallabs/gem'
import {
  gemCopilotOpenSelector,
  gemPanelInlineSize,
  gemPanelTransition,
} from '@opallabs/gem-copilot/src/shared'
import { modernWithFallback } from '@opallabs/lambda'
import { DrawerOrigin, DrawerSize, drawerSizes } from './types'

/**
 * Marks a subtree the drawer's box-sizing reset leaves alone. See
 * +legacyBoxModel+ below for why exclusion beats imposing the opposite reset.
 */
export const LEGACY_BOX_MODEL_ATTRIBUTE = 'data-legacy-box-model'

/** Matches an element that is neither the marked subtree's root nor inside it. */
const OUTSIDE_LEGACY_BOX_MODEL =
  `:not([${LEGACY_BOX_MODEL_ATTRIBUTE}]):not([${LEGACY_BOX_MODEL_ATTRIBUTE}] *)` as const

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
})

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
})

// Extra space to account for drawer shadow
const POSITION_OFFSET = '40px'
// Incorporates extra space into the offscreen value,
// which is used below to animate the drawer in and out
const OFFSCREEN = `calc(100% + ${POSITION_OFFSET})`

export const moveIn = (drawerOrigin: DrawerOrigin) => {
  let from,
    to,
    signModifier = 1
  switch (drawerOrigin) {
    case DrawerOrigin.Top:
      signModifier = -1
    // falls through
    case DrawerOrigin.Bottom:
      from = {
        transform: `translateY(calc(${OFFSCREEN} * ${signModifier}))`,
      }
      to = { transform: 'translateY(0)' }
      break
    case DrawerOrigin.Left:
      signModifier = -1
    // falls through
    case DrawerOrigin.Right:
      from = {
        transform: `translateX(calc(${OFFSCREEN} * ${signModifier}))`,
      }
      to = { transform: 'translateX(0)' }
      break
  }
  return keyframes({ from, to })
}

export const moveOut = (drawerOrigin: DrawerOrigin) => {
  let from,
    to,
    signModifier = 1
  switch (drawerOrigin) {
    case DrawerOrigin.Top:
      signModifier = -1
    // falls through
    case DrawerOrigin.Bottom:
      to = {
        transform: `translateY(calc(${OFFSCREEN} * ${signModifier}))`,
      }
      from = { transform: 'translateY(0)' }
      break
    case DrawerOrigin.Left:
      signModifier = -1
    // falls through
    case DrawerOrigin.Right:
      to = {
        transform: `translateX(calc(${OFFSCREEN} * ${signModifier}))`,
      }
      from = { transform: 'translateX(0)' }
      break
  }
  return keyframes({ from, to })
}

export const Overlay: CSSObject = {
  position: 'fixed',
  inset: 0,
  zIndex: ZIndex.Drawer.Overlay,
  backgroundColor: wideGamutColor('#000', 0.15),

  '&[data-state="open"]': {
    animation: `${fadeIn} 500ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },

  '&[data-state="closed"]': {
    animation: `${fadeOut} 500ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },
}

export const drawerWrapper = (
  drawerOrigin: DrawerOrigin,
  drawerSize: DrawerSize
): CSSObject => ({
  display: 'flex',
  flexDirection: 'column',
  fontFamily: Typeface.Default,
  overflow: 'hidden',
  position: 'fixed',
  zIndex: ZIndex.Drawer.Content,
  backgroundColor: `var(${ThemedColors.Surface1})`,
  ...Shadow[ShadowElevation.E3],

  // Render with a border radius when origin is top or bottom
  borderTopLeftRadius: drawerOrigin === DrawerOrigin.Bottom ? 16 : undefined,
  borderTopRightRadius: drawerOrigin === DrawerOrigin.Bottom ? 16 : undefined,
  borderBottomLeftRadius: drawerOrigin === DrawerOrigin.Top ? 16 : undefined,
  borderBottomRightRadius: drawerOrigin === DrawerOrigin.Top ? 16 : undefined,

  // Support for origin placement variance
  insetInlineStart: drawerOrigin === DrawerOrigin.Left ? 0 : undefined,
  insetInlineEnd:
    drawerOrigin === DrawerOrigin.Bottom ||
    drawerOrigin === DrawerOrigin.Right ||
    drawerOrigin === DrawerOrigin.Top
      ? 0
      : undefined,
  insetBlockStart:
    drawerOrigin === DrawerOrigin.Left ||
    drawerOrigin === DrawerOrigin.Right ||
    drawerOrigin === DrawerOrigin.Top
      ? 0
      : undefined,
  insetBlockEnd: drawerOrigin === DrawerOrigin.Bottom ? 0 : undefined,

  // Support for size variance based on origin
  blockSize:
    drawerOrigin === DrawerOrigin.Bottom || drawerOrigin === DrawerOrigin.Top
      ? drawerSizes[drawerSize]
      : '100%',
  inlineSize:
    drawerOrigin === DrawerOrigin.Left || drawerOrigin === DrawerOrigin.Right
      ? drawerSizes[drawerSize]
      : '100%',

  '&[data-state="open"]': {
    animation: `${moveIn(drawerOrigin)} 500ms ease`,
  },

  '&[data-state="closed"]': {
    animation: `${moveOut(drawerOrigin)} 500ms ease`,
  },

  '&[data-drawer-origin="right"]': {
    transition: 'inset-inline-end 300ms ease',
  },

  [`.${gemCopilotOpenSelector} &[data-drawer-origin="right"]`]: {
    insetInlineEnd: gemPanelInlineSize,
    transition: `inset-inline-end ${gemPanelTransition} ease`,
  },
  // 'border-box' is set globally in Asgard, but not in legacy views.
  // We need to set it here for all descendant elements so the layout
  // works correctly in a legacy view — except inside a subtree marked with
  // LEGACY_BOX_MODEL_ATTRIBUTE, which opts out entirely (see below).
  '&': {
    boxSizing: 'border-box',
  },
  [`& *${OUTSIDE_LEGACY_BOX_MODEL}`]: {
    boxSizing: 'border-box',
  },
})

/**
 * Spread onto an element to opt its subtree out of the drawer's `border-box`
 * reset, for content that was not authored against it.
 *
 * The reset reaches every descendant, which is right for the drawer's own
 * contents and wrong for anything a drawer merely hosts. Older trees size
 * themselves against the browser default: they subtract their own padding by
 * hand, pairing (say) `padding: 11px 16px` with `width: calc(100% - 32px)`.
 * Under `border-box` that padding comes off twice.
 *
 * The subtree is *excluded* rather than forced to `content-box`. Foreign content
 * is not uniformly one box model: the same tree holds elements that declare
 * `border-box` themselves (a card with `width: 375px` and its padding inside),
 * and a blanket `content-box` overrides those and pushes their padding back out.
 * Excluding leaves every element on its own declaration, or on the browser
 * default where it has none — which is the environment the content was written
 * in and still renders in outside this drawer.
 *
 * Mark only the foreign subtree — never the drawer's own components, which are
 * built for `border-box` and shrink by their own padding without it.
 */
export const legacyBoxModel = { [LEGACY_BOX_MODEL_ATTRIBUTE]: '' } as const

export const drawerHeader = {
  backgroundColor: `var(${ThemedColors.Surface2})`,
  padding: Spacing.L,
  position: 'relative',
} satisfies CSSObject

export const drawerBody = css({
  display: 'block',
  flexGrow: 1,
  padding: Spacing.L,
  ...modernWithFallback(
    [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
    'overflowBlock: auto'
  ),
})

export const drawerFooter = {
  columnGap: Spacing.XS * 3,
  display: 'flex',
  justifyContent: 'flex-start',
  padding: Spacing.XS * 3,
  borderBlockStart: `1px solid var(${ThemedColors.StrokeSecondary1})`,
} satisfies CSSObject

export const title = {
  ...Typography.H300,
  color: `var(${ThemedColors.TextPrimary})`,
  fontWeight: FontWeight.Bold,
  margin: 0,
  marginBlockEnd: Spacing.S,
} satisfies CSSObject

export const description = {
  color: `var(${ThemedColors.TextSecondary})`,
  margin: 0,
} satisfies CSSObject

export const close = {
  color: `var(${ThemedColors.Surface1})`,
} satisfies CSSObject

export const drawerClose: CSSObject = {
  blockSize: Spacing.L,
  inlineSize: Spacing.L,
  insetBlockStart: Spacing.L,
  insetInlineEnd: Spacing.L,
  position: 'absolute',
}
